import React from "react";

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  FieldCategory,
  FieldValueType,
} from "@safetyculture/s12-apis-connect-web/s12/typefield/v1/field_pb";
import { FieldStatus } from "@safetyculture/s12-apis-connect-web/s12/users/userfield/v1/field_pb";

import mainStyles from "../../../styles";
import CheckboxFilter from "../../shared/checkbox-filter";
import {
  FIELD_CATEGORY_OPTIONS,
  FIELD_STATUS_OPTIONS,
  SELECTABLE_DATA_TYPES,
  dataTypeDisplayNames,
} from "./utils";

interface UserFieldsFiltersProps {
  status: FieldStatus;
  setStatus: (status: FieldStatus) => void;
  category: FieldCategory;
  setCategory: (category: FieldCategory) => void;
  dataTypes: FieldValueType[];
  setDataTypes: (dataTypes: FieldValueType[]) => void;
  disabled?: boolean;
}

export const UserFieldsFilters = ({
  status,
  setStatus,
  category,
  setCategory,
  dataTypes,
  setDataTypes,
  disabled,
}: UserFieldsFiltersProps) => {
  const classes = mainStyles();

  const handleStatusChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: FieldStatus | null,
  ) => {
    // Exclusive toggle returns null when the active button is re-clicked.
    // Keep a status selected at all times.
    if (value !== null) {
      setStatus(value);
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent<FieldCategory>) => {
    setCategory(Number(event.target.value) as FieldCategory);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        marginTop: 3,
        marginBottom: 2,
      }}
      data-testid="user-fields-filters"
    >
      <ToggleButtonGroup
        value={status}
        exclusive
        size="small"
        color="primary"
        onChange={handleStatusChange}
        aria-label="user fields status view"
        disabled={disabled}
        data-testid="user-fields-status-filter"
      >
        {FIELD_STATUS_OPTIONS.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            color="primary"
            aria-label={option.label}
            sx={classes.toogleButtonColor}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="user-fields-category-label">Category</InputLabel>
        <Select
          labelId="user-fields-category-label"
          label="Category"
          value={category}
          onChange={handleCategoryChange}
          disabled={disabled}
          data-testid="user-fields-category-filter"
        >
          {FIELD_CATEGORY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <CheckboxFilter<FieldValueType>
        title="Data types"
        filterList={dataTypes}
        setFilterList={setDataTypes}
        allItems={[...SELECTABLE_DATA_TYPES]}
        displayNamesMapping={dataTypeDisplayNames}
        size="small"
        disabled={disabled}
        testId="user-fields-data-type-filter"
        selectAllTestId="user-fields-data-type-filter-select-all"
      />
    </Box>
  );
};
